import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X, Camera, MagnifyingGlass, CheckCircle, Crosshair,
} from '@phosphor-icons/react';
import { apiFormaleta, medidasDe, textoSobre } from '@/api/formaleta';
import type {
  AltaResultado, CatalogosFormaleta, ModeloFormaleta, RenglonFormaleta,
} from '@/api/formaleta';
import { Aviso, Chip, Foto, Pastilla, SinRegistrar } from './comunes';
import { subirFotoFormaleta } from './subirFoto';

// INVENTARIAR — rediseñado el 5-nov-2026 para el día que arranca el inventario
// físico. Daniel lo probó en el celular y lo aprobó así.
//
// Lo que manda la forma de esta pantalla es UNA cosa: se usa de pie, con una
// mano, con guante, al sol, y se repite cientos de veces seguidas. Cada toque
// que se pueda sacar vale más que cualquier campo que se pueda agregar.
//
//   · El NOMBRE va arriba y pegado, y el fondo de esa franja ES el color de la
//     categoría elegida. No es decoración: es el único control visual contra el
//     error caro de esta pantalla —inventariar 40 piezas con la categoría de la
//     pila anterior—. El color se ve de reojo; un texto no.
//   · Las MEDIDAS van abajo y fijas, donde llega el pulgar, con teclado
//     numérico. Es lo único que se teclea en cada pieza.
//   · La CATEGORÍA es una rejilla de 5×2: las diez visibles, un solo toque,
//     sin scroll horizontal (pedido explícito de Daniel: el scroll obligaba a
//     buscar).
//   · El FONDO no se muestra NI entra al nombre. Prácticamente todos los
//     paneles son de 5 cm, así que repetirlo solo alargaba el código que hay
//     que leer en el aluminio. El backend lo sigue guardando en `Dimension3`.
//   · La UBICACIÓN, la OBRA y el botón «usar mi ubicación» se fueron. El GPS
//     corre solo con `watchPosition` y la coordenada se guarda sin que nadie
//     la pida. Decisión de Daniel: NO se condiciona a que existan los
//     polígonos —la coordenada se guarda cruda y el lote se resuelve después
//     con un barrido contra `geo.Geometria`—.
//   · No hay contador de «cuántas iguales hay». Se fue a propósito: creaba N
//     piezas compartiendo una sola foto y una sola coordenada, o sea N filas
//     con el mismo dato. Una por una, cada pieza tiene lo suyo. Lo que sí
//     queda es la categoría PEGAJOSA entre piezas, que es donde estaba la
//     ganancia real de velocidad.

type Modo = 'existente' | 'nuevo';

interface Geo { lat: number; lng: number; precision: number }

export function PanelAlta({ catalogos, inicial, onCerrar }: {
  catalogos: CatalogosFormaleta;
  /** De dónde se abrió: una tarjeta de modelo/renglón, o la vista por defecto. */
  inicial?: { modelo: ModeloFormaleta; idForm?: number } | null;
  onCerrar: () => void;
}) {
  const qc = useQueryClient();

  // Antes arrancaba SIEMPRE en «existente». Ahora el modo lo decide de dónde
  // se abrió: con una tarjeta detrás, el operario ya eligió el modelo; sin
  // nada detrás estamos en la vista por defecto de campo, que es alta de
  // modelo nuevo —lo que se va a hacer cientos de veces mañana—.
  const [modo, setModo] = useState<Modo>(inicial?.modelo ? 'existente' : 'nuevo');
  const [modelo, setModelo] = useState<ModeloFormaleta | null>(inicial?.modelo ?? null);
  const [idForm, setIdForm] = useState<number | null>(inicial?.idForm ?? null);

  // Modelo nuevo. El fondo ya no es estado de la pantalla: no se pregunta.
  const [idCategoria, setIdCategoria] = useState<number | null>(null);
  const [ancho, setAncho] = useState('');
  const [largo, setLargo] = useState('');

  const [idEstado, setIdEstado] = useState<number | null>(
    catalogos.estados.find(e => e.disponible)?.id ?? null);
  const [marcada, setMarcada] = useState(false);
  const [foto, setFoto] = useState<File | null>(null);
  /** La última pieza guardada. NO abre una pantalla: pinta una tira fina bajo
   *  la franja y el formulario sigue ahí, listo para la siguiente.
   *
   *  Antes acá había una vista de éxito a pantalla completa. Daniel la vio en
   *  campo el 5-nov-2026 y la pregunta fue la correcta: «no sé de dónde sale
   *  ese AD-00002 ni qué aporta». El código SÍ aporta —es el consecutivo único
   *  de ESA pieza física, el que hay que grabar en el aluminio para que el
   *  panel y la fila de la base sean el mismo objeto—, pero no justifica
   *  frenar la tanda con una pantalla y dos toques de más. Se queda el dato,
   *  se va el muro. */
  const [ultima, setUltima] = useState<AltaResultado | null>(null);
  const [avisoFoto, setAvisoFoto] = useState<string | null>(null);

  const inputFoto = useRef<HTMLInputElement>(null);
  const refAncho = useRef<HTMLInputElement>(null);
  const urlPrevia = useMemo(() => (foto ? URL.createObjectURL(foto) : null), [foto]);
  useEffect(() => () => { if (urlPrevia) URL.revokeObjectURL(urlPrevia); }, [urlPrevia]);

  // ── GPS silencioso ──────────────────────────────────────────────────────
  // `watchPosition` y no `getCurrentPosition`: la primera lectura de un
  // teléfono suele venir de la antena (cientos de metros) y mejora en segundos
  // cuando engancha satélites. Con `watch` la pieza se guarda con la MEJOR
  // lectura disponible en ese momento en vez de con la primera.
  // `precision` se muestra en pantalla a propósito: si dice ±300 m el operario
  // puede caminar dos pasos afuera antes de guardar.
  const [geo, setGeo] = useState<Geo | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  useEffect(() => {
    if (!navigator.geolocation) {
      setGeoError('Este dispositivo no da ubicación.');
      return;
    }
    const id = navigator.geolocation.watchPosition(
      p => {
        setGeoError(null);
        setGeo({
          lat: +p.coords.latitude.toFixed(6),
          lng: +p.coords.longitude.toFixed(6),
          precision: Math.round(p.coords.accuracy * 10) / 10,
        });
      },
      e => setGeoError(e.message || 'No se pudo leer la ubicación.'),
      { enableHighAccuracy: true, maximumAge: 30_000, timeout: 20_000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  const cat = catalogos.categorias.find(c => c.id === idCategoria) ?? null;
  const nAncho = numero(ancho);
  const nLargo = numero(largo);

  // Vista previa del nombre. ⚠️ Es una RÉPLICA de ArmarNombre del backend, que
  // es el que manda: acá solo sirve para que el operario vea qué va a quedar
  // antes de guardar. Si las dos divergen, la que vale es la del servidor.
  // Desde el 5-nov-2026 NO lleva el fondo: `PAE-60X260`, no `PAE-60X260X5`.
  const nombrePrevio = cat?.abreviatura && nAncho !== null && nLargo !== null
    ? `${cat.abreviatura.toUpperCase()}-${fmt(nAncho)}X${fmt(nLargo)}`
    : null;

  // El color de la franja de arriba. En modo nuevo lo da la categoría elegida;
  // con un modelo ya elegido, el suyo. Sin nada, el gris de marca.
  const colorFranja = (modo === 'nuevo' ? cat?.colorHex : modelo?.colorHex) || '#5D636C';
  const tinta = textoSobre(colorFranja);
  const nombreFranja = modo === 'nuevo' ? nombrePrevio : (modelo?.nombre ?? null);

  const alta = useMutation({
    mutationFn: () => apiFormaleta.inventariar({
      idForm: modo === 'existente' ? idForm : null,
      nuevoModelo: modo === 'nuevo'
        // `fondo: null` y el backend pone 5. La pantalla no lo pregunta, pero
        // el dato existe: lo que se fue es su copia dentro del nombre.
        ? { idCategoria, ancho: nAncho, largo: nLargo, fondo: null, unidad: 'EA' }
        : null,
      // Una pieza por alta. `cuantas` sigue siendo obligatorio en el DTO (es
      // `int` no nullable: omitirlo deserializa a 0 y el backend tira 400).
      cuantas: 1,
      idEstado,
      latitud: geo?.lat ?? null,
      longitud: geo?.lng ?? null,
      precisionGpsM: geo?.precision ?? null,
      marcadaAhora: marcada,
    }),
    onSuccess: async (r) => {
      setUltima(r);
      // Volver al formulario YA, sin esperar a que suba la foto: la pieza está
      // guardada y lo que el operario necesita es el próximo campo, no un
      // cartel. Si la foto falla, el aviso aparece en la tira sin interrumpir.
      limpiarParaLaSiguiente();
      // La foto se sube DESPUÉS del alta porque hasta acá no existe a qué
      // colgarla. Va al RENGLÓN y no a la pieza cuando el modelo todavía no
      // tiene foto: eso arregla el hueco de verdad (0 fotos en 509 renglones) y
      // sirve para todas las piezas del modelo, no solo para la que se acaba de
      // encontrar.
      if (foto) {
        try {
          const alRenglon = !modelo?.urlFoto || r.piezas.length === 0;
          // `subirFotoFormaleta` comprime (3–6 MB → ~250 KB) y prueba el carril
          // directo al blob antes de caer al endpoint de siempre. Ver F-3 en
          // `subirFoto.ts`: la compresión es la que hace la diferencia en obra.
          const res = alRenglon
            ? await subirFotoFormaleta('renglon', r.idForm, foto)
            : await subirFotoFormaleta('pieza', r.piezas[0]!.id, foto);
          setAvisoFoto(res.advertencia);
        } catch (e) {
          // La pieza YA quedó inventariada: una foto que no subió no puede
          // leerse como que el alta falló.
          setAvisoFoto(`La pieza se guardó, pero la foto no subió: ${(e as Error).message}`);
        }
      }
      qc.invalidateQueries({ queryKey: ['formaleta'] });
    },
  });

  const puedeGuardar = modo === 'existente'
    ? idForm !== null
    : idCategoria !== null && nAncho !== null && nLargo !== null;

  /** Deja el formulario listo para la pieza siguiente: limpia lo que es DE LA
   *  PIEZA y conserva lo que es de la TANDA. La categoría y el estado quedan
   *  porque en una tanda real se inventarían cuarenta paneles de la misma
   *  pila; las medidas, la foto y la marca se van porque son de esta pieza y
   *  de ninguna otra. */
  function limpiarParaLaSiguiente() {
    setFoto(null);
    setAvisoFoto(null);
    setAncho('');
    setLargo('');
    setMarcada(false);
    // En campo el siguiente gesto es teclear el ancho: se le deja el foco.
    requestAnimationFrame(() => refAncho.current?.focus());
  }

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center">
      <div className="flex h-full w-full max-w-xl flex-col overflow-hidden bg-white sm:h-auto sm:max-h-[92vh] sm:rounded-xl">

        {/* ── NOMBRE STICKY · el fondo ES la categoría ─────────────────── */}
        <header
          className="shrink-0 px-4 pb-2.5 pt-3 transition-colors duration-200"
          style={{ background: colorFranja, color: tinta }}
        >
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-bold uppercase tracking-[0.1em] opacity-60">
                Nombre que va a quedar
              </p>
              <p className={`mt-0.5 break-all font-mono font-bold leading-tight ${
                nombreFranja ? 'text-[26px]' : 'text-sm font-normal opacity-60'
              }`}>
                {nombreFranja ?? 'elegí categoría y medidas'}
              </p>
            </div>
            <button type="button" onClick={onCerrar} aria-label="Cerrar"
              className="-mr-1.5 shrink-0 rounded p-2 opacity-70 hover:opacity-100">
              <X size={18} weight="bold" />
            </button>
          </div>

          {/* Pastillas de contexto. El GPS no se pide: se informa. */}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <PastillaFranja tinta={tinta}>
              <Crosshair size={11} weight="bold" />
              {geo
                ? `GPS ±${fmt(geo.precision)} m`
                : geoError ? 'sin GPS' : 'buscando GPS…'}
            </PastillaFranja>
            {geo && (
              <PastillaFranja tinta={tinta} tenue>
                coordenada guardada · lote se resuelve luego
              </PastillaFranja>
            )}
            {modo === 'nuevo' && <PastillaFranja tinta={tinta}>AD01</PastillaFranja>}
          </div>
        </header>

        {/* ── TIRA DE LA ÚLTIMA GUARDADA ──────────────────────────────
            No interrumpe: el formulario ya está limpio y enfocado debajo. El
            consecutivo se queda a la vista hasta que se guarde la siguiente,
            porque marcarlo en el aluminio es un acto aparte que puede pasar
            minutos después (de ahí el check «ya le pegué la marca»). Un toast
            de tres segundos se iría antes de que el operario agarre la
            grabadora. */}
        {ultima && (
          <div className="shrink-0 border-b border-brand-green-100 bg-brand-green-100/15 px-4 py-2">
            <div className="flex items-center gap-2">
              <CheckCircle size={17} weight="fill" className="shrink-0 text-brand-green-200" />
              <span className="min-w-0 flex-1 text-[13px] leading-tight text-brand-gray-500">
                Guardada{' '}
                <span className="font-mono text-base font-bold text-brand-gray-500">
                  {ultima.piezas[0]?.consecutivo ?? ultima.nombre}
                </span>
                {' '}— grabá ese código en el aluminio
              </span>
              <button type="button" onClick={() => { setUltima(null); setAvisoFoto(null); }}
                aria-label="Ocultar" className="shrink-0 p-1 text-brand-gray-400">
                <X size={14} weight="bold" />
              </button>
            </div>
            {avisoFoto && (
              <p className="mt-1 text-[11px] font-semibold text-brand-red-200">{avisoFoto}</p>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 pb-2 pt-3">
                {!catalogos.piezasHabilitadas && (
                  <div className="mb-4">
                    <Aviso
                      tono="alerta"
                      titulo="El alta todavía no se puede guardar"
                      detalle={catalogos.motivoDeshabilitado}
                      pista="El formulario se puede llenar y revisar, pero el botón de guardar va a devolver este mismo motivo hasta que la migración corra."
                    />
                  </div>
                )}

                <div className="mb-4 flex gap-2">
                  <Chip texto="Un modelo nuevo" activo={modo === 'nuevo'}
                    onClick={() => setModo('nuevo')} />
                  <Chip texto="Un modelo que ya existe" activo={modo === 'existente'}
                    onClick={() => setModo('existente')} />
                </div>

                {/* ── Foto · arriba, antes que cualquier dato ── */}
                <Campo etiqueta="Foto">
                  <button type="button" onClick={() => inputFoto.current?.click()}
                    className={`flex w-full items-center gap-3 rounded-lg border-2 p-2 text-left ${
                      foto
                        ? 'border-brand-green-100 bg-brand-green-100/10'
                        : 'border-dashed border-brand-gray-200 hover:bg-brand-bg'
                    }`}>
                    <Foto url={urlPrevia} alt="foto de la pieza" lado={56} />
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-brand-gray-500">
                      <Camera size={17} weight="bold" />
                      {foto ? 'Foto lista — tocá para repetir' : 'Tomar foto'}
                    </span>
                  </button>
                  <input ref={inputFoto} type="file" accept="image/*" capture="environment" hidden
                    onChange={e => { setFoto(e.target.files?.[0] ?? null); e.target.value = ''; }} />
                </Campo>

                {modo === 'existente'
                  ? <ElegirExistente
                      modelo={modelo} idForm={idForm}
                      onModelo={m => { setModelo(m); setIdForm(null); }}
                      onRenglon={setIdForm}
                    />
                  : (
                    <Campo etiqueta="Categoría">
                      {/* 5 columnas: las diez categorías entran en dos filas y
                          se eligen con un solo toque. Sin scroll. */}
                      <div className="grid grid-cols-5 gap-1.5">
                        {catalogos.categorias.map(c => {
                          const activo = idCategoria === c.id;
                          const hex = c.colorHex || '#6A7062';
                          return (
                            <button
                              key={c.id} type="button" onClick={() => setIdCategoria(c.id)}
                              title={c.nombre || undefined}
                              className={`flex aspect-[1/0.92] flex-col items-center justify-center gap-0.5 rounded-lg border-2 px-0.5 ${
                                activo ? 'border-black' : 'border-transparent'
                              }`}
                              style={{ background: hex, color: textoSobre(hex) }}
                            >
                              <span className="font-mono text-[13px] font-bold leading-none">
                                {c.abreviatura || '???'}
                              </span>
                              <span className="line-clamp-2 text-center text-[7px] font-medium leading-tight opacity-90">
                                {c.nombre || 'sin nombre'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      {/* Acá vivía el croquis en vivo. Daniel lo sacó el 5-nov-2026
                          probándolo en el celular: era lo único que empujaba
                          la pantalla más allá del alto del teléfono, y en campo
                          el scroll cuesta más de lo que el dibujo aporta. */}

                    </Campo>
                  )}

                {/* ── Estado · dos columnas ── */}
                {catalogos.estados.length > 0 ? (
                  <Campo etiqueta="Estado">
                    <div className="grid grid-cols-2 gap-1.5">
                      {catalogos.estados.map(e => (
                        <button
                          key={e.id} type="button" onClick={() => setIdEstado(e.id)}
                          className={`flex min-h-[44px] items-center gap-2 rounded-lg border px-3 text-left text-[13px] ${
                            idEstado === e.id
                              ? 'border-brand-gray-500 bg-brand-gray-500 font-bold text-white'
                              : 'border-brand-gray-200 font-medium text-brand-gray-500 hover:bg-brand-bg'
                          }`}
                        >
                          <span className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ background: e.colorHex || '#AAAFB6' }} />
                          <span className="min-w-0 truncate">{e.estado}</span>
                        </button>
                      ))}
                    </div>
                  </Campo>
                ) : (
                  <Campo etiqueta="Estado">
                    <SinRegistrar
                      que="el catálogo de estados todavía no existe"
                      detalle={catalogos.motivoDeshabilitado || undefined}
                    />
                  </Campo>
                )}

                <Campo etiqueta="Marca física"
                  ayuda="Marcarla es un hecho aparte de inventariarla: una pieza puede entrar hoy a la base y recibir su marca una semana después.">
                  <label className="flex items-center gap-2 text-sm text-brand-gray-500">
                    <input type="checkbox" checked={marcada}
                      onChange={e => setMarcada(e.target.checked)}
                      className="h-5 w-5 accent-brand-green-200" />
                    Ya le pegué la marca
                  </label>
                </Campo>

                {geoError && (
                  <p className="mb-3 text-[11px] text-brand-red-200">
                    {geoError} La pieza se puede inventariar igual: queda sin coordenada.
                  </p>
                )}

                {alta.isError && (
                  <div className="mb-3">
                    <Aviso tono="alerta" titulo="No se pudo inventariar"
                      detalle={(alta.error as Error).message} />
                  </div>
                )}
        </div>

        {/* ── MEDIDAS ABAJO · donde llega el pulgar ────────────────────── */}
        <footer className="shrink-0 border-t border-brand-gray-100 bg-white px-4 pb-4 pt-2.5 shadow-[0_-6px_16px_rgba(0,0,0,0.05)]">
            {modo === 'nuevo' && (
              <div className="mb-2 flex items-end gap-2">
                <Medida etiqueta="Ancho cm" valor={ancho} onCambio={setAncho} refInput={refAncho} />
                <Medida etiqueta="Largo cm" valor={largo} onCambio={setLargo} />
              </div>
            )}
            <button
              type="button"
              disabled={!puedeGuardar || alta.isPending}
              onClick={() => alta.mutate()}
              className="w-full rounded-lg bg-brand-green-100 py-4 text-base font-bold text-black hover:bg-brand-green-200 disabled:bg-brand-gray-200 disabled:text-brand-gray-400"
            >
              {alta.isPending ? 'Guardando…' : 'Inventariar pieza'}
            </button>
        </footer>
      </div>
    </div>
  );
}

/** Pastilla de la franja de color. El fondo se deriva de la tinta para que se
 *  lea sobre cualquiera de los diez hex de categoría. */
function PastillaFranja({ tinta, tenue, children }: {
  tinta: string;
  tenue?: boolean;
  children: ReactNode;
}) {
  const fondo = tinta === '#FFFFFF' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${
        tenue ? 'opacity-80' : ''
      }`}
      style={{ background: fondo, color: tinta }}
    >
      {children}
    </span>
  );
}

// ── Elegir un modelo que ya existe ──────────────────────────────────────────

function ElegirExistente({ modelo, idForm, onModelo, onRenglon }: {
  modelo: ModeloFormaleta | null;
  idForm: number | null;
  onModelo: (m: ModeloFormaleta) => void;
  onRenglon: (id: number) => void;
}) {
  const [q, setQ] = useState('');

  const { data: encontrados } = useQuery({
    queryKey: ['formaleta', 'modelos', 'alta', q],
    queryFn: () => apiFormaleta.modelos({ q }),
    enabled: !modelo && q.trim().length >= 2,
  });

  const { data: detalle } = useQuery({
    queryKey: ['formaleta', 'modelo', modelo?.nombre],
    queryFn: () => apiFormaleta.modelo(modelo!.nombre),
    enabled: !!modelo,
  });

  // Con un solo renglón no se pregunta: elegir entre uno es una fricción
  // gratuita en una pantalla que se usa con guante.
  useEffect(() => {
    const rs = detalle?.renglones;
    if (rs && rs.length === 1 && idForm === null) onRenglon(rs[0]!.idForm);
  }, [detalle, idForm, onRenglon]);

  if (!modelo) {
    return (
      <Campo etiqueta="¿Qué pieza es?" ayuda="Buscá por código, categoría o medida.">
        <div className="flex items-center gap-2 rounded-lg border border-brand-gray-200 px-3">
          <MagnifyingGlass size={16} weight="bold" className="text-brand-gray-300" />
          <input
            value={q} onChange={e => setQ(e.target.value)}
            placeholder="PARAPET, CR-90X120, pretil…"
            className="flex-1 py-3 text-sm text-brand-gray-500 placeholder:text-brand-gray-300 focus:outline-none"
          />
        </div>
        <div className="mt-2 max-h-64 space-y-1.5 overflow-y-auto">
          {(encontrados || []).slice(0, 12).map(m => (
            <button key={m.nombre} type="button" onClick={() => onModelo(m)}
              className="flex w-full items-center gap-2 rounded-lg border border-brand-gray-200 p-2 text-left hover:bg-brand-bg">
              {m.abreviatura
                ? <Pastilla texto={m.abreviatura} colorHex={m.colorHex} />
                : <SinRegistrar que="sin cat." />}
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-bold text-brand-gray-500">
                  {medidasDe(m) || 'sin medidas'}
                </span>
                <span className="block truncate font-mono text-[10px] text-brand-gray-400">
                  {m.nombre}
                </span>
              </span>
              <span className="shrink-0 font-mono text-[11px] text-brand-gray-400">
                {m.piezasDeclaradas}
              </span>
            </button>
          ))}
          {q.trim().length >= 2 && encontrados && encontrados.length === 0 && (
            <p className="py-2 text-xs text-brand-gray-400">
              Nada con ese texto. Si la pieza es de nuestra planta, probablemente
              todavía no existe como modelo: usá «Un modelo nuevo».
            </p>
          )}
        </div>
      </Campo>
    );
  }

  return (
    <Campo etiqueta="¿De qué renglón es?"
      ayuda="Un mismo modelo puede venir de varios pedidos. El renglón es la trazabilidad con el fabricante.">
      <div className="mb-2 flex items-center gap-2 rounded-lg bg-brand-bg p-2">
        {modelo.abreviatura && <Pastilla texto={modelo.abreviatura} colorHex={modelo.colorHex} />}
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-bold text-brand-gray-500">
            {medidasDe(modelo) || 'sin medidas'}
          </span>
          <span className="block truncate font-mono text-[10px] text-brand-gray-400">
            {modelo.nombre}
          </span>
        </span>
      </div>
      <div className="space-y-1.5">
        {(detalle?.renglones || []).map((r: RenglonFormaleta) => (
          <button key={r.idForm} type="button" onClick={() => onRenglon(r.idForm)}
            className={`flex w-full items-center gap-2 rounded-lg border p-2.5 text-left ${
              idForm === r.idForm
                ? 'border-brand-gray-500 bg-brand-gray-500/5'
                : 'border-brand-gray-200 hover:bg-brand-bg'
            }`}>
            {idForm === r.idForm && (
              <CheckCircle size={16} weight="fill" className="shrink-0 text-brand-green-200" />
            )}
            <span className="min-w-0 flex-1 text-xs font-semibold text-brand-gray-500">
              {r.pedido || 'sin pedido'}
              <span className="ml-2 font-mono text-[10px] font-normal text-brand-gray-400">
                #{r.idForm}
              </span>
            </span>
            <span className="shrink-0 font-mono text-[11px] text-brand-gray-400">
              {r.piezasInventariadas} / {r.piezasDeclaradas}
            </span>
          </button>
        ))}
      </div>
    </Campo>
  );
}

// ── Piezas chicas del formulario ────────────────────────────────────────────

function Campo({ etiqueta, ayuda, children }: {
  etiqueta: string;
  ayuda?: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-4">
      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.09em] text-brand-gray-300">
        {etiqueta}
      </p>
      {children}
      {ayuda && <p className="mt-1.5 text-[11px] leading-snug text-brand-gray-400">{ayuda}</p>}
    </div>
  );
}

/** Medida del pie: alta, mono y grande. 26 px no es capricho — por debajo de
 *  16 px Safari iOS hace auto-zoom al enfocar el input y descuadra la pantalla. */
function Medida({ etiqueta, valor, onCambio, refInput }: {
  etiqueta: string;
  valor: string;
  onCambio: (v: string) => void;
  refInput?: React.RefObject<HTMLInputElement>;
}) {
  return (
    <label className="min-w-0 flex-1">
      {/* Negro y grande a propósito (pedido de Daniel, 5-nov-2026): es LA
          medida que el operario teclea en cada pieza y tiene que saber sin
          dudar cuál está escribiendo. El gris claro de 9 px que usa el resto
          del formulario acá se leía como decoración. */}
      <span className="block text-[13px] font-bold uppercase tracking-[0.06em] text-black">
        {etiqueta}
      </span>
      <input
        ref={refInput}
        type="text" inputMode="decimal" autoComplete="off"
        value={valor}
        onChange={e => onCambio(e.target.value.replace(/[^\d.]/g, '').slice(0, 6))}
        placeholder="0"
        className="mt-0.5 h-14 w-full rounded-lg border-2 border-brand-gray-200 bg-brand-bg text-center font-mono text-[26px] font-bold text-brand-gray-500 placeholder:text-brand-gray-300 focus:border-brand-green-100 focus:bg-white focus:outline-none"
      />
    </label>
  );
}

function numero(v: string): number | null {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** `40` y no `40.00`, igual que el `Medida()` del backend. */
function fmt(n: number): string {
  return String(Number(n));
}
